import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { UIDReset } from 'react-uid';

import { TOOLBAR_HOSTS } from 'lib/posthog';

import CouponDeepLinkProvider from './CouponDeepLinkProvider';
import ExperimentProvider from './ExperimentProvider';
import GlobalQueriesProvider from './GlobalQueriesProvider';
import LocalWishlistProvider from './LocalWishlistProvider';
import PostHogProvider from './PostHogProvider';
import QueryClientProvider from './QueryClientProvider';
import ThemeProvider from './ThemeProvider';
import TopbarProvider from './TopbarProvider';
import UserProvider from './UserProvider';
import UtmProvider from './UtmProvider';
import WebViewProvider from './WebViewProvider';

// dynamic() always imports the real module — never return a fake
// object from the factory. The host check happens in React land
// via ConditionalDevToolbar to avoid the "Element type is invalid"
// error caused by Promise.resolve({ default: () => null }).
const DevToolbar = dynamic(() => import('components/DevToolbar'), {
  ssr: false,
});

const ConditionalDevToolbar = () => {
  if (
    typeof window === 'undefined' ||
    !TOOLBAR_HOSTS.includes(window.location.hostname)
  ) {
    return null;
  }

  return <DevToolbar />;
};

const GlobalProvider = ({
  activeOffersListTab,
  categories,
  children,
  experiments = null,
  featuredLandings,
  highlightedLandings,
  navbarAds,
  posthogFlags = null,
  stores,
  topbarAd,
}) => (
  <UIDReset>
    <WebViewProvider>
      <QueryClientProvider>
        <GlobalQueriesProvider
          activeOffersListTabName={activeOffersListTab}
          categories={categories}
          featuredLandings={featuredLandings}
          highlightedLandings={highlightedLandings}
          navbarAds={navbarAds}
          stores={stores}
          topbarAd={topbarAd}
        >
          <LocalWishlistProvider>
            <ExperimentProvider ssrActiveExperiments={experiments}>
              <PostHogProvider bootstrapFlags={posthogFlags}>
                <ThemeProvider>
                  <TopbarProvider isTopbarAdActive={!!topbarAd}>
                    <UserProvider>
                      <UtmProvider>
                        <CouponDeepLinkProvider>
                          <ConditionalDevToolbar />
                          {children}
                        </CouponDeepLinkProvider>
                      </UtmProvider>
                    </UserProvider>
                  </TopbarProvider>
                </ThemeProvider>
              </PostHogProvider>
            </ExperimentProvider>
          </LocalWishlistProvider>
        </GlobalQueriesProvider>
      </QueryClientProvider>
    </WebViewProvider>
  </UIDReset>
);

GlobalProvider.propTypes = {
  activeOffersListTab: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.shape()),
  ssrHeaders: PropTypes.shape({
    ssrAuthToken: PropTypes.string,
  }),
  children: PropTypes.node.isRequired,
  featuredLandings: PropTypes.shape({
    landings: PropTypes.arrayOf(PropTypes.shape()),
  }),
  experiments: PropTypes.shape(),
  posthogFlags: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  ),
  highlightedLandings: PropTypes.shape({
    landings: PropTypes.arrayOf(PropTypes.shape()),
  }),
  stores: PropTypes.arrayOf(PropTypes.shape()),
  navbarAds: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string,
      notificationIcon: PropTypes.bool.isRequired,
      position: PropTypes.number.isRequired,
      sameTab: PropTypes.bool.isRequired,
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ),
  topbarAd: PropTypes.shape({
    imageSrc: PropTypes.string,
    description: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    isClosable: PropTypes.bool.isRequired,
    bgcolor: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
};

export default GlobalProvider;
